import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Beaker,
  Search,
  Filter,
  Clock,
  Award,
  ArrowRight,
  Loader2,
  Target,
  Brain,
  Building2,
  Sparkles,
  SlidersHorizontal,
  X,
  Zap,
  Play,
  Users,
  Shield,
  Cpu,
  Activity,
  Plus,
  Route
} from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Breadcrumb } from '../components/ui/Breadcrumb';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import { cn } from '../lib/utils';
import { API_BASE } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

const API = `${API_BASE}/api`;

const INDUSTRIES = [
  'All Industries',
  'Financial Services',
  'Healthcare',
  'Technology',
  'Manufacturing',
  'Retail',
  'Government',
  'Education',
  'Legal',
  'Energy',
  'General Business'
];

const TOPICS = [
  'All Topics',
  'Anti-Corruption & Bribery',
  'Data Privacy & Protection',
  'Conflict of Interest',
  'Cybersecurity',
  'Workplace Ethics',
  'Anti-Money Laundering',
  'Health & Safety',
  'Harassment Prevention',
  'Information Security',
  'Regulatory Compliance'
];

const LEVELS = [
  { value: 'all', label: 'All Levels' },
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' }
];

// =========================
// MATRIX DATA RAIN BACKGROUND
// =========================
function MatrixRain() {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%^&*()αβγδεζηθικλμνξοπρστυφχψω';
    const charArray = chars.split('');
    const fontSize = 14;
    const columns = canvas.width / fontSize;
    const drops = Array(Math.floor(columns)).fill(1);
    
    const draw = () => {
      ctx.fillStyle = 'rgba(9, 9, 11, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      ctx.fillStyle = 'rgba(30, 191, 179, 0.12)'; // Cerebro teal tint
      ctx.font = `${fontSize}px monospace`;
      
      for (let i = 0; i < drops.length; i++) {
        const text = charArray[Math.floor(Math.random() * charArray.length)];
        ctx.fillText(text, i * fontSize, drops[i] * fontSize);
        
        if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i]++;
      }
    };
    
    const interval = setInterval(draw, 50);
    
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none opacity-30 z-0"
      style={{ mixBlendMode: 'screen' }}
    />
  );
}

// =========================
// AI BRAIN WAVE HEADER
// =========================
function AIBrainHeader() {
  return (
    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1F3C88]/40 via-navigate-black to-[#1EBFB3]/20 border border-[#1EBFB3]/20 mb-8">
      {/* Animated background grid */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(30,191,179,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(30,191,179,0.03)_1px,transparent_1px)] bg-[size:50px_50px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_110%)]" />
      
      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-[#1EBFB3] rounded-full animate-float-particle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${3 + Math.random() * 4}s`,
              opacity: 0.3 + Math.random() * 0.4,
            }}
          />
        ))}
      </div>
      
      {/* Synapse lines */}
      <svg className="absolute inset-0 w-full h-full opacity-20" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="synapseGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#1EBFB3" stopOpacity="0" />
            <stop offset="50%" stopColor="#C8A85A" stopOpacity="1" />
            <stop offset="100%" stopColor="#1EBFB3" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[...Array(8)].map((_, i) => (
          <line
            key={i}
            x1={`${10 + i * 12}%`}
            y1="100%"
            x2={`${50 + (Math.random() - 0.5) * 30}%`}
            y2="20%"
            stroke="url(#synapseGrad)"
            strokeWidth="1"
            className="animate-synapse-pulse"
            style={{ animationDelay: `${i * 0.3}s` }}
          />
        ))}
      </svg>
      
      {/* Content */}
      <div className="relative z-10 px-8 py-12 md:py-16 flex flex-col md:flex-row items-center gap-8">
        {/* Brain Icon with animations */}
        <div className="relative">
          {/* Outer glow rings */}
          <div className="absolute inset-0 -m-4 rounded-full bg-[#1EBFB3]/20 animate-ping-slow" />
          <div className="absolute inset-0 -m-2 rounded-full bg-[#C8A85A]/10 animate-pulse-slow" />
          
          {/* Brain container */}
          <div className="relative w-32 h-32 md:w-40 md:h-40 bg-gradient-to-br from-[#1F3C88] via-[#1F3C88] to-[#1EBFB3] rounded-full flex items-center justify-center shadow-2xl shadow-[#1EBFB3]/30">
            {/* Inner glow */}
            <div className="absolute inset-2 bg-gradient-to-br from-[#1EBFB3]/50 to-[#C8A85A]/30 rounded-full animate-pulse" />
            
            {/* Brain icon */}
            <Brain className="relative z-10 h-16 w-16 md:h-20 md:w-20 text-white drop-shadow-lg" />
            
            {/* Electrical pulses */}
            <div className="absolute top-2 right-4 w-2 h-2 bg-[#1EBFB3] rounded-full animate-pulse" />
            <div className="absolute bottom-4 left-2 w-1.5 h-1.5 bg-[#C8A85A] rounded-full animate-pulse" style={{ animationDelay: '0.5s' }} />
            <div className="absolute top-1/2 right-0 w-1 h-1 bg-white rounded-full animate-ping" />
          </div>
          
          {/* Orbiting elements */}
          <div className="absolute inset-0 -m-8 animate-spin-slow">
            <Cpu className="absolute top-0 left-1/2 -translate-x-1/2 h-5 w-5 text-[#1EBFB3]" />
            <Zap className="absolute bottom-0 left-1/2 -translate-x-1/2 h-5 w-5 text-[#C8A85A]" />
            <Activity className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-5 text-[#1EBFB3]" />
            <Shield className="absolute right-0 top-1/2 -translate-y-1/2 h-5 w-5 text-[#C8A85A]" />
          </div>
        </div>
        
        {/* Text content */}
        <div className="text-center md:text-left flex-1">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#1EBFB3]/20 border border-[#1EBFB3]/30 rounded-full mb-4">
            <Sparkles className="h-4 w-4 text-[#1EBFB3] animate-pulse" />
            <span className="text-sm font-medium text-[#1EBFB3]">Interactive Simulations</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Learning{' '}
            <span className="bg-gradient-to-r from-[#1EBFB3] via-[#C8A85A] to-[#1EBFB3] bg-clip-text text-transparent animate-gradient-x">
              Labs
            </span>
          </h1>
          
          <p className="text-lg text-zinc-300 max-w-xl mb-6">
            Immersive scenario-based training. 
            Practice real-world governance decisions in a safe environment.
          </p>
          
          <div className="flex flex-wrap items-center gap-4 justify-center md:justify-start">
            <div className="flex items-center gap-2 text-sm text-zinc-400">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <span>Adaptive AI Responses</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-zinc-400">
              <div className="w-2 h-2 bg-[#C8A85A] rounded-full animate-pulse" />
              <span>Real-time Feedback</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-zinc-400">
              <div className="w-2 h-2 bg-[#1EBFB3] rounded-full animate-pulse" />
              <span>Personalized Learning</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// =========================
// HOLOGRAPHIC LAB CARD
// =========================
function HolographicLabCard({ lab }) {
  const { token, isAuthenticated } = useAuth();
  const [isHovered, setIsHovered] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [pathways, setPathways] = useState([]);
  const [loadingPathways, setLoadingPathways] = useState(false);
  const [addingToPathway, setAddingToPathway] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const cardRef = useRef(null);
  
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  
  const fetchPathways = async () => {
    if (pathways.length > 0) return;
    setLoadingPathways(true);
    try {
      const response = await axios.get(`${API}/learning-pathways`, { headers });
      // Response is an array directly
      const pathwayList = Array.isArray(response.data) ? response.data : (response.data.pathways || []);
      setPathways(pathwayList.filter(p => p.is_published));
    } catch (error) {
      console.error('Failed to fetch pathways:', error);
    } finally {
      setLoadingPathways(false);
    }
  };
  
  const handleAddToPathway = async (e, pathwayId, pathwayName) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      toast.error('Please sign in to add labs to your pathway');
      return;
    }
    
    setAddingToPathway(true);
    try {
      const response = await axios.post(`${API}/competencies/pathway/add-item`, {
        pathway_id: pathwayId,
        item_type: 'learning_lab',
        item_id: lab.lab_id
      }, { headers });
      
      if (response.data.already_added) {
        toast.info(`Already in "${pathwayName}"`);
      } else {
        toast.success(`Added to "${pathwayName}"!`);
      }
      setDropdownOpen(false);
    } catch (error) {
      console.error('Failed to add to pathway:', error);
      toast.error('Failed to add to pathway');
    } finally {
      setAddingToPathway(false);
    }
  };
  
  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setMousePosition({ x, y });
  };
  
  const gradientStyle = isHovered ? {
    background: `radial-gradient(circle at ${mousePosition.x * 100}% ${mousePosition.y * 100}%, rgba(30, 191, 179, 0.15), transparent 50%)`,
  } : {};
  
  const tiltStyle = isHovered ? {
    transform: `perspective(1000px) rotateY(${(mousePosition.x - 0.5) * 10}deg) rotateX(${(0.5 - mousePosition.y) * 10}deg) translateZ(10px)`,
  } : {};
  
  return (
    <Link
      to={`/labs/${lab.lab_id}`}
      ref={cardRef}
      className="group relative block"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseMove={handleMouseMove}
      data-testid={`lab-card-${lab.lab_id}`}
    >
      {/* Holographic border glow */}
      <div className={cn(
        "absolute -inset-0.5 rounded-2xl opacity-0 transition-opacity duration-500 blur-sm",
        "bg-gradient-to-r from-[#1EBFB3] via-[#C8A85A] to-[#1EBFB3] bg-[length:200%_100%]",
        isHovered && "opacity-70 animate-gradient-x"
      )} />
      
      {/* Card */}
      <div 
        className={cn(
          "relative bg-zinc-900/90 backdrop-blur-sm border border-zinc-800 rounded-2xl overflow-hidden transition-all duration-300",
          isHovered && "border-[#1EBFB3]/50 shadow-xl shadow-[#1EBFB3]/10"
        )}
        style={tiltStyle}
      >
        {/* Holographic overlay */}
        <div 
          className="absolute inset-0 pointer-events-none transition-opacity duration-300 z-10"
          style={gradientStyle}
        />
        
        {/* Thumbnail */}
        <div className="relative h-48 overflow-hidden">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#1F3C88]/60 via-zinc-900 to-[#1EBFB3]/30" />
          
          {/* Grid pattern */}
          <div className="absolute inset-0 bg-[linear-gradient(rgba(30,191,179,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(30,191,179,0.05)_1px,transparent_1px)] bg-[size:20px_20px]" />
          
          {/* Image or placeholder */}
          {lab.thumbnail_url ? (
            <img
              src={lab.thumbnail_url}
              alt={lab.title}
              className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-60"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative">
                <Beaker className="h-20 w-20 text-[#1EBFB3]/30" />
                <div className="absolute inset-0 animate-pulse">
                  <Beaker className="h-20 w-20 text-[#1EBFB3]/20" />
                </div>
              </div>
            </div>
          )}
          
          {/* Scanline effect */}
          <div className="absolute inset-0 bg-[linear-gradient(transparent_50%,rgba(0,0,0,0.1)_50%)] bg-[size:100%_4px] pointer-events-none opacity-30" />
          
          {/* Level Badge */}
          <div className="absolute top-3 left-3 z-20">
            <span className={cn(
              "px-3 py-1.5 text-xs font-semibold rounded-full backdrop-blur-sm border",
              lab.level === 'beginner' 
                ? 'bg-green-500/20 text-green-300 border-green-500/30' 
                : lab.level === 'advanced' 
                  ? 'bg-red-500/20 text-red-300 border-red-500/30' 
                  : 'bg-[#C8A85A]/20 text-[#C8A85A] border-[#C8A85A]/30'
            )}>
              {lab.level?.charAt(0).toUpperCase() + lab.level?.slice(1)}
            </span>
          </div>
          
          {/* AI Badge with glow */}
          {lab.ai_generated && (
            <div className="absolute top-3 right-3 z-20">
              <div className="relative">
                <div className="absolute inset-0 bg-[#1EBFB3] rounded-full blur-md opacity-50 animate-pulse" />
                <span className="relative px-3 py-1.5 text-xs font-semibold bg-[#1EBFB3]/30 text-[#1EBFB3] rounded-full flex items-center gap-1.5 backdrop-blur-sm border border-[#1EBFB3]/40">
                  <Sparkles className="h-3 w-3 animate-pulse" />
                  AI-Powered
                </span>
              </div>
            </div>
          )}
          
          {/* Play button overlay */}
          <div className={cn(
            "absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300 z-20",
            isHovered && "opacity-100"
          )}>
            <div className="w-16 h-16 rounded-full bg-[#1EBFB3]/80 backdrop-blur-sm flex items-center justify-center transform scale-75 group-hover:scale-100 transition-transform duration-300 shadow-lg shadow-[#1EBFB3]/30">
              <Play className="h-8 w-8 text-white ml-1" />
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="relative p-5">
          {/* Industry tag */}
          {lab.industry && (
            <div className="flex items-center gap-2 mb-3">
              <Building2 className="h-3.5 w-3.5 text-zinc-500" />
              <span className="text-xs text-zinc-500">{lab.industry}</span>
            </div>
          )}
          
          {/* Title */}
          <h3 className="font-bold text-lg text-white mb-2 group-hover:text-[#1EBFB3] transition-colors line-clamp-2">
            {lab.title}
          </h3>
          
          {/* Description */}
          <p className="text-sm text-zinc-400 line-clamp-2 mb-4">
            {lab.description || lab.scenario_overview}
          </p>
          
          {/* Meta info */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5 text-xs text-zinc-500">
                <Clock className="h-3.5 w-3.5" />
                <span>{lab.estimated_time || '15-20 min'}</span>
              </div>
              {lab.completions > 0 && (
                <div className="flex items-center gap-1.5 text-xs text-zinc-500">
                  <Users className="h-3.5 w-3.5" />
                  <span>{lab.completions}</span>
                </div>
              )}
            </div>
            
            {/* XP Badge */}
            <div className="flex items-center gap-1 px-2 py-1 bg-navigate-gold/10 rounded-full">
              <Zap className="h-3 w-3 text-navigate-gold" />
              <span className="text-xs font-medium text-navigate-gold">+300 XP</span>
            </div>
          </div>
          
          {/* Add to Pathway & Hover arrow */}
          <div className="flex items-center gap-2">
            {isAuthenticated && (
              <DropdownMenu open={dropdownOpen} onOpenChange={(open) => {
                setDropdownOpen(open);
                if (open) fetchPathways();
              }}>
                <DropdownMenuTrigger asChild>
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    className="p-1.5 rounded-lg bg-zinc-800 hover:bg-teal-500/20 text-zinc-400 hover:text-teal-400 transition-colors z-20"
                    title="Add to Pathway"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-zinc-900 border-zinc-700 w-56" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenuLabel className="text-zinc-400 text-xs">Add to Learning Pathway</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-zinc-700" />
                  {loadingPathways ? (
                    <div className="flex items-center justify-center py-3">
                      <Loader2 className="h-4 w-4 animate-spin text-teal-500" />
                    </div>
                  ) : pathways.length === 0 ? (
                    <div className="px-2 py-3 text-center text-xs text-zinc-500">
                      No pathways available
                    </div>
                  ) : (
                    pathways.map((pathway) => (
                      <DropdownMenuItem
                        key={pathway.pathway_id}
                        onClick={(e) => handleAddToPathway(e, pathway.pathway_id, pathway.title || pathway.name)}
                        className="text-white hover:bg-zinc-800 cursor-pointer text-sm"
                        disabled={addingToPathway}
                      >
                        <Route className="h-4 w-4 mr-2 text-teal-400" />
                        <span className="truncate">{pathway.title || pathway.name}</span>
                      </DropdownMenuItem>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            
            {/* Hover arrow */}
            <div className={cn(
              "transition-all duration-300",
              isHovered ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-2"
            )}>
              <ArrowRight className="h-5 w-5 text-[#1EBFB3]" />
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

// =========================
// MAIN COMPONENT
// =========================
export default function LearningLabCatalog() {
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filters
  const [search, setSearch] = useState('');
  const [industry, setIndustry] = useState('All Industries');
  const [topic, setTopic] = useState('All Topics');
  const [level, setLevel] = useState('all');

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchLabs();
  }, [industry, topic, level]);

  const fetchLabs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (industry && industry !== 'All Industries') params.append('industry', industry);
      if (topic && topic !== 'All Topics') params.append('topic', topic);
      if (level && level !== 'all') params.append('level', level);
      if (search) params.append('search', search);

      const res = await axios.get(`${API}/labs?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      setLabs(res.data.labs || []);
    } catch (error) {
      console.error('Failed to fetch labs:', error);
      setLabs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchLabs();
  };

  const clearFilters = () => {
    setSearch('');
    setIndustry('All Industries');
    setTopic('All Topics');
    setLevel('all');
  };

  const hasActiveFilters = search || industry !== 'All Industries' || topic !== 'All Topics' || level !== 'all';

  return (
    <Layout showBreadcrumb={false}>
      <div className="relative min-h-screen">
        {/* Breadcrumb */}
        <div className="relative z-20 max-w-7xl mx-auto px-4 pt-4">
          <Breadcrumb 
            items={[
              { label: 'Dashboard', href: '/dashboard', isRoot: true },
              { label: 'Learning Labs', isCurrent: true }
            ]} 
          />
        </div>
        
        {/* Matrix Rain Background */}
        <MatrixRain />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 py-8">
          {/* AI Brain Header */}
          <AIBrainHeader />
          
          {/* Search & Filters */}
          <div className="bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-2xl p-6 mb-8">
            <form onSubmit={handleSearch} className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
                <Input
                  type="text"
                  placeholder="Search learning labs..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-12 bg-zinc-800/50 border-zinc-700 h-12 text-white placeholder:text-zinc-500 focus:border-[#1EBFB3] focus:ring-[#1EBFB3]/20"
                />
              </div>
              
              {/* Filter toggle button */}
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className={cn(
                  "h-12 border-zinc-700 text-zinc-300 hover:text-white hover:border-[#1EBFB3]",
                  showFilters && "border-[#1EBFB3] text-[#1EBFB3]"
                )}
              >
                <SlidersHorizontal className="h-4 w-4 mr-2" />
                Filters
                {hasActiveFilters && (
                  <span className="ml-2 w-2 h-2 bg-[#1EBFB3] rounded-full" />
                )}
              </Button>
              
              <Button
                type="submit"
                className="h-12 bg-gradient-to-r from-[#1EBFB3] to-[#1F3C88] hover:from-[#25D4C7] hover:to-[#2A4FA0] text-white border-0"
              >
                Search Labs
              </Button>
            </form>
            
            {/* Expanded Filters */}
            {showFilters && (
              <div className="mt-6 pt-6 border-t border-zinc-800">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-zinc-400">Filter by:</span>
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="text-sm text-[#1EBFB3] hover:text-[#25D4C7] flex items-center gap-1"
                    >
                      <X className="h-3 w-3" />
                      Clear all
                    </button>
                  )}
                </div>
                
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm text-zinc-400 mb-2 block">Industry</label>
                    <Select value={industry} onValueChange={setIndustry}>
                      <SelectTrigger className="bg-zinc-800/50 border-zinc-700 focus:border-[#1EBFB3]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {INDUSTRIES.map((ind) => (
                          <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400 mb-2 block">Topic</label>
                    <Select value={topic} onValueChange={setTopic}>
                      <SelectTrigger className="bg-zinc-800/50 border-zinc-700 focus:border-[#1EBFB3]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TOPICS.map((t) => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400 mb-2 block">Difficulty</label>
                    <Select value={level} onValueChange={setLevel}>
                      <SelectTrigger className="bg-zinc-800/50 border-zinc-700 focus:border-[#1EBFB3]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LEVELS.map((l) => (
                          <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Results */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-[#1EBFB3]/20 rounded-full" />
                <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-[#1EBFB3] rounded-full animate-spin" />
                <Brain className="absolute inset-0 m-auto h-6 w-6 text-[#1EBFB3]" />
              </div>
              <p className="mt-4 text-zinc-400">Loading AI simulations...</p>
            </div>
          ) : labs.length === 0 ? (
            <div className="text-center py-20">
              <div className="relative inline-block mb-6">
                <div className="w-24 h-24 bg-zinc-800/50 rounded-full flex items-center justify-center">
                  <Beaker className="h-12 w-12 text-zinc-600" />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-[#1EBFB3]/20 rounded-full flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-[#1EBFB3]" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">No Learning Labs Found</h3>
              <p className="text-zinc-400 mb-6">
                {hasActiveFilters ? 'Try adjusting your filters' : 'Check back soon for new AI-powered scenarios'}
              </p>
              {hasActiveFilters && (
                <Button
                  onClick={clearFilters}
                  variant="outline"
                  className="border-[#1EBFB3]/50 text-[#1EBFB3] hover:bg-[#1EBFB3]/10"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-6">
                <p className="text-zinc-400">
                  <span className="text-white font-medium">{labs.length}</span> simulation{labs.length !== 1 ? 's' : ''} available
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {labs.map((lab, index) => (
                  <div
                    key={lab.lab_id}
                    className="animate-fade-in-up"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <HolographicLabCard lab={lab} />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
